import React, { useEffect, useRef, useState } from 'react';
import Editor from 'src/components/Editor/DynamicEditor';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { RootState } from 'src/store/reducers/state';
import { useEventActions } from 'src/store/event/actions';
import { ImageStringConverter } from 'src/components/utilities/utils';
import Loader from 'src/components/loader';

const BODY_BEFORE =
    "<div class='w-100 ck ck-content'><p>Dear %FirstName%,&nbsp;</p><p><br>This is a reminder that %EventName% is happening on %Date% at %Time%.</p><p>&nbsp;</p><p>Here's a quick rundown of the details:&nbsp;</p><p><br><strong>Title</strong>: %Title%&nbsp;<br><strong>Date</strong>: %Date%&nbsp;<br><strong>Time</strong>: %Time%&nbsp;<br><strong>Duration</strong>: %duration%&nbsp;<br><strong>Link</strong>: <a href=\"%HostUrl%\">%HostUrl%</a></p><p>&nbsp;</p><p><strong>Accessing the Event:</strong>&nbsp;</p><p>Click on the link above, and simply log in with your email address prior to the start time of the event.&nbsp;<br>Whilst it is recommended that you attend the event live, a recording will be available the next day and accessible via the same link above.</p><p>&nbsp;</p><p>Thank you and see you online soon!</p></div>";

const BODY_AFTER =
    "<div class='w-100 ck ck-content'><p>Hi %FirstName%,</p><p><br>Thank you for your interest in our webinar %Title%.</p><p><br>We're excited to let you know that it is now available on-demand. Whether you attended the live session or missed it, you can now access the full recording at your convenience.</p><p><br>Access the On-Demand Webinar:%HostUrl%</p><p><br>If you have any questions or need further assistance, feel free to reach out to us.</p><p><br>Thank you for being a part of our community. We look forward to your continued participation and hope to see you at our future events!</p></div>";

interface IProps {
    reminderDetails: any | null;
    modalFlag: 'add' | 'edit';
    nextReminderNumber: number;
}

const EventReminderModal: React.FC<IProps> = ({ reminderDetails, modalFlag, nextReminderNumber }) => {
    const dispatch = useDispatch();
    const EventAction = useEventActions(dispatch);
    const { eventId } = useParams<string>();
    const btnRef = useRef<HTMLButtonElement>(null);
    const [heading, setHeading] = useState('Add');

    const { isEventReminderUpdateLoading, isEventReminderUpdated } = useSelector((state: RootState) => ({
        isEventReminderUpdateLoading: state.event.isEventReminderUpdateLoading,
        isEventReminderUpdated: state.event.isEventReminderUpdated
    }));

    const defaultValues =
        modalFlag === 'edit' && reminderDetails
            ? reminderDetails
            : {
                id: '',
                eventId: eventId,
                emailReminderNumber: nextReminderNumber,
                emailReminderType: 1,
                emailReminder: true,
                emailRemiderTriggerInMinutes: 60,
                emailReminderSubject: 'Reminder - %EventName%',
                emailReminderBody: BODY_BEFORE
            };

    const { register, handleSubmit, control, reset, setValue } = useForm({ defaultValues });


    const handleEditorSetValue = (_path: string, value: any) => {
        setValue('emailReminderBody', value, { shouldDirty: true });
    };

    useEffect(() => {
        if (modalFlag === 'edit' && reminderDetails) {
            reset(reminderDetails);
            setHeading('Edit');
        } else {
            reset({
                id: '',
                eventId: eventId,
                emailReminderNumber: nextReminderNumber,
                emailReminderType: 1,
                emailReminder: true,
                emailRemiderTriggerInMinutes: 60,
                emailReminderSubject: 'Reminder - %EventName%',
                emailReminderBody: BODY_BEFORE
            });
            setHeading('Add');
        }
    }, [reminderDetails, modalFlag, nextReminderNumber]);

    const handleReminderTypeChange = (value: any) => {
        setValue('emailReminderBody', value == 1 ? BODY_BEFORE : BODY_AFTER);
        setValue(
            'emailReminderSubject',
            value == 1 ? 'Reminder - %EventName%' : 'Access Your Webinar On-Demand Now!'
        );
    };

    const onSubmit = (data: any) => {
        if (!eventId) return;
        const payloadData = {
            ...(data.id ? { id: data.id } : {}),
            eventId,
            emailReminder: data.emailReminder,
            emailRemiderTriggerInMinutes: data.emailRemiderTriggerInMinutes,
            emailReminderSubject: data.emailReminderSubject,
            emailReminderBody: ImageStringConverter(data.emailReminderBody),
            emailReminderNumber: data.emailReminderNumber,
            emailReminderType: data.emailReminderType
        };
        if (modalFlag === 'add') {
            EventAction.addEventReminderRequest(payloadData);
        } else {
            EventAction.updateEventReminderRequest(payloadData);
        }

    };

    useEffect(() => {
        if (isEventReminderUpdated) {
            if (btnRef.current) {
                btnRef.current.click();
                EventAction.resetEventStateRequest()
            }
        }
    }, [isEventReminderUpdated])
    return (
        <div
            className="modal fade fixed top-0 left-0 hidden w-full h-full outline-none overflow-x-hidden overflow-y-auto"
            id="eventReminderModal"
            aria-labelledby="eventReminderModalLabel"
            aria-hidden="true">
            <div className="modal-dialog relative w-auto pointer-events-none max-w-2xl mx-2 sm:mx-auto">
                <div className="modal-content border-none shadow-lg relative flex flex-col w-full pointer-events-auto bg-white bg-clip-padding rounded-md outline-none text-current">
                    <div className="relative modal-header flex flex-shrink-0 items-center justify-between p-4 pb-2 rounded-t-md">
                        <h5 className="text-lg sm:text-xl font-semibold leading-normal text-center w-full" id="eventReminderModalLabel">
                            {`${heading} Reminder ${modalFlag === 'edit' ? reminderDetails?.emailReminderNumber : ''}`}
                        </h5>
                        <button
                            ref={btnRef}
                            type="button"
                            className="dismiss-btn"
                            data-bs-dismiss="modal"
                            aria-label="Close"></button>
                    </div>
                    <div className="modal-body relative py-4 px-4 sm:px-10">
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <input type="hidden" {...register('id')} />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
                                <div className="form-group form-switch pl-0">
                                    <label className="block mb-1">Turn on email reminder</label>
                                    <input
                                        type="checkbox"
                                        className="form-check-input switch-btn"
                                        id="emailReminder"
                                        {...register('emailReminder')}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="block mb-1">When to send</label>
                                    <select
                                        {...register('emailReminderType')}
                                        className="input-style"
                                        onChange={(e) => handleReminderTypeChange(e.target.value)}>
                                        <option value={1}>Before</option>
                                        <option value={2}>After</option>
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 xl:gap-4 lg:gap-4 md:gap-2 mb-4">
                                <div className="form-group">
                                    <label className="block mb-1">Reminder sent</label>
                                    <select {...register('emailRemiderTriggerInMinutes')} className="input-style">
                                        <option value={60}>1 hour</option>
                                        <option value={120}>2 hours</option>
                                        <option value={240}>4 hours</option>
                                        <option value={720}>12 hours</option>
                                        <option value={1440}>24 hours</option>
                                        <option value={2880}>48 hours</option>
                                        <option value={4320}>72 hours</option>
                                        <option value={5760}>96 hours</option>
                                        <option value={10080}>1 week</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="block mb-1">Email Reminder Subject</label>
                                    <input type="text" className="input-style" {...register('emailReminderSubject')} />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 mb-4">
                                <div className="form-group">
                                    <label className="block mb-1">Email Reminder Body</label>
                                    <Controller
                                        name="emailReminderBody"
                                        control={control}
                                        render={({ field }) => (
                                            <div className="w-full max-w-full overflow-x-auto">
                                                <Editor data={field.value} setValue={handleEditorSetValue} index={0} />
                                            </div>
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="modal-footer flex flex-shrink-0 items-center justify-center gap-2 p-4 pb-6 pt-0 rounded-b-md">
                                <button disabled={isEventReminderUpdateLoading} type="submit" className="px-6 btn-primary flex-1 sm:flex-none sm:w-28 mr-0 ml-0 mt-0">
                                    Save
                                </button>
                                <button type="button" className="mx-0 px-6 btn-primary discard-btn flex-1 sm:flex-none sm:w-28 mt-0" data-bs-dismiss="modal">
                                    Close
                                </button>
                                {isEventReminderUpdateLoading && (
                                    <div className="ml-2">
                                        <Loader />
                                    </div>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EventReminderModal;